<template>
  <div style="width:600px" class="bonus-rule  flex flex-col mt-14 relative">

    <div v-if="errorMessage" class="error-label mb-2 self-start text-left">{{errorMessage}}</div>

    <!-- Priority setter -->
    <div v-if="bonusRulesCount>1" class="priority  absolute align-center flex flex-col -left-12 text-center -top-1">
      <fai :icon="['far', 'chevron-up']"
           :class="{ disabled: bonusRule.priority === 0 }"
           @click="moveRuleUp"
           class="chevron  pointer self-center"
      />
      <span class="position  bg-blue h-5 rounded-full text-sm text-white w-5">{{ bonusRule.priority + 1 }}</span>
      <fai :icon="['far', 'chevron-down']"
           :class="{ disabled: bonusRule.priority === bonusRulesCount-1 }"
           @click="moveRuleDown"
           class="chevron  pointer self-center"
      />
    </div>

    <!-- Content of the rule -->
    <div class="flex w-full">
      <span class="p-2 pl-0 text-xl">if</span>
      <!-- Event type -->
      <sb-async-dropdown
        :rows.sync="eventDropdown.rows"
        :selected.sync="eventDropdown.selected"
        :focused.sync="eventDropdown.focused"
        :options="eventDropdown.options"
        @update:selected="updatedSelectedEvent"
        class="dropdown-component m-0"
        :class="{hasError: !eventDropdown.selected}"
      />
      <span v-if="!!eventDropdown.selected" class="p-2 text-xl">is</span>

      <!-- Event value -->
      <sb-async-dropdown
        v-if="inputType === 'select'"
        @update:selected="updateSelectedEventOption"
        :rows.sync="eventOptionsDropdown.rows"
        :selected.sync="eventOptionsDropdown.selected"
        :focused.sync="eventOptionsDropdown.focused"
        :options="eventOptionsDropdown.options"
        class="dropdown-component event-option m-0"
        :class="{hasError: !eventOptionsDropdown.selected}"
      >
        <template #label-only="{label}">
          <div class="label-text">
            {{label}}
          </div>
        </template>
      </sb-async-dropdown>

      <!-- Integer range -->
      <div v-if="inputType === 'integer_range'" class="integer-range  flex flex-auto">
        <input
          type="number"
          name="event_range_min"
          v-model="bonusRule.event_range_min"
          placeholder="min"
          min="0"
          :max="bonusRule.event_range_max ? Math.round(bonusRule.event_range_max)-1 : 9999999"
          @keydown="preventNegativeNumber"
          class="flex-auto m-0 sb-label"
          :class="{hasError: !isIntegerRangeValid}"
        />
        <span class="p-2 text-xl">to</span>
        <input
          type="number"
          name="event_range_max"
          v-model="bonusRule.event_range_max"
          placeholder="max"
          :min="Math.round(bonusRule.event_range_min)+1"
          max="10000000"
          @keydown="preventNegativeNumber"
          class="flex-auto m-0 sb-label"
          :class="{hasError: !isIntegerRangeValid}"
        />
      </div>

      <!-- Time range -->
      <div v-if="inputType === 'time_range'" class="date-range flex flex-auto">
        <sb-date
          :flatpickr="{ onClose: selectedDates => this.eventRangeMin = selectedDates[0] }"
          :value.sync="bonusRule.event_range_min"
          :include="['time']"
          format="hh:mm aa"
          placeholder="start time"
          class="flex-auto m-0 sb-white-input"
          :class="{hasError: !isTimeRangeValid}"
        />
        <span class="text-xl p-2">to</span>
        <sb-date
          :flatpickr="{ onClose: selectedDates => this.eventRangeMax = selectedDates[0] }"
          :value.sync="bonusRule.event_range_max"
          :include="['time']"
          format="hh:mm aa"
          placeholder="end time"
          class="flex-auto m-0 sb-white-input"
          :class="{hasError: !isTimeRangeValid}"
        />
      </div>
    </div>

    <!-- Points earned -->
    <div v-if="isEventOptionValid" class="flex flex-row items-center mt-5 self-start">
      <label :for="'bonus-points'+bonusRule.priority" class="py-3 text-xl">then, member earns</label>
      <input
        type="number"
        :step="bonusPointsStep" :min="bonusPointsMin" max="100000"
        v-model="bonusRule.points"
        @keydown="preventNegativeNumber"
        :id="'bonus-points'+bonusRule.priority"
        class="m-0 mx-3 sb-label text-center w-auto"
        :class="{hasError: !isBonusPointsValid}"
      >
      <label :for="'bonus-points'+bonusRule.priority" class="py-3 text-xl"> {{ parseFloat(bonusRule.points) | pluralize('point') }} per {{ programTypeEvent }}</label>
    </div>

    <!-- Delete rule -->
    <a class="absolute -right-10 text-gray-400 top-3" @click="$emit('deleteBonusRule', bonusRule)">&times;</a>

  </div>
</template>

<script>

import { bonusRulesAPI } from "../../services/api/bonusRulesAPI";

export default {
  name: "BonusEngineRule",
  props: {
    bonusRule: Object,
    programType: String,
    eventTypes: Array,
    eventValues: Array,
    bonusRulesCount: Number
  },
  data() {
    return {
      eventDropdown: {
        rows: ['loading-row'],
        selected: null,
        focused: false,
        options: {
          label: selected => selected ? selected.label?.toLowerCase() : 'select an event'
        }
      },
      eventsOptionsLabels: {
        "day_of_the_week": "day of week",
        "time_of_day_range": "time of day",
        "category_purchased": "category",
        "brand_purchased": "brand",
        "transaction_total_range": "total",
        "points_earned_range": "points earned",
        "transaction_location": "location",
        "order_source": "order source",
        'reward_redeemed': 'reward',
        'offer_redeemed': 'offer',
      },
      eventOptionsDropdown: {
        rows: ['loading-row'],
        selected: null,
        focused: false,
        options: {
          label: selected => selected?.label?.toLowerCase() || `select ${this.eventsOptionsLabels[this.eventDropdown.selected?.key]}`
        }
      },
      errorMessage: null
    }
  },
  computed: {
    inputType() {
      return this.eventDropdown.selected?.input_type
    },
    programTypeEvent() {
      return this.programType === "points_per_dollar" ? "dollar" : "visit"
    },
    bonusPointsStep() {
      return this.programType === "points_per_dollar" ? 0.01 : 1
    },
    bonusPointsMin() {
      return this.programType === "points_per_dollar" ? 0.01 : 1
    },
    isEventValueValid() {
      return this.bonusRule.event_value !== null
    },
    isTimeRangeValid() {
      return !(
        [null, ''].includes(this.bonusRule.event_range_min)
        || [null, ''].includes(this.bonusRule.event_range_max)
        || this.bonusRule.event_range_min >= this.bonusRule.event_range_max
      )
    },
    isIntegerRangeValid() {
      return !(
        [null, ''].includes(this.bonusRule.event_range_min)
        || [null, ''].includes(this.bonusRule.event_range_max)
        || !Number.isInteger(parseInt(this.bonusRule.event_range_min))
        || !Number.isInteger(parseInt(this.bonusRule.event_range_max))
        || parseInt(this.bonusRule.event_range_min) >= parseInt(this.bonusRule.event_range_max)
      )
    },
    isEventOptionValid() {
      let isValid = false;
      if(this.inputType==="select") {
        isValid = this.isEventValueValid
      }else if(this.inputType==="integer_range") {
        isValid = this.isIntegerRangeValid
      }else if(this.inputType==="time_range") {
        isValid = this.isTimeRangeValid
      }
      return isValid
    },
    isBonusPointsValid() {
      if(this.programType === "points_per_dollar") {
        return this.isPointsPerDollarValid;
      }else{
        return this.isPointsPerVisitValid;
      }
    },
    isPointsPerDollarValid() {
      return this.bonusRule.points !== null
        && parseFloat(this.bonusRule.points) >= 0.01
        && parseFloat(this.bonusRule.points) <= 100000
        && Math.round(this.bonusRule.points * 100) / 100 === parseFloat(this.bonusRule.points)
        && !isNaN(parseFloat(this.bonusRule.points))
    },
    isPointsPerVisitValid() {
      return this.bonusRule.points !== null
        && parseFloat(this.bonusRule.points) >= 1
        && parseFloat(this.bonusRule.points) <= 100000
        && Number.isInteger(parseFloat(this.bonusRule.points))
        && !isNaN(parseFloat(this.bonusRule.points))
    },
    isRuleValid() {
      return this.isEventOptionValid && this.isBonusPointsValid
    },
  },
  async created() {
    this.eventDropdown.rows = this.eventTypes.map( row => (
      {
        slot: 'select-row',
        state: row
      }
    ))
    this.eventDropdown.selected = this.eventDropdown.rows.find( row => row.state.key === this.bonusRule.event_type )?.state
    if(this.inputType==='select') this.computeEventOptionsDropdownRows();
    this.eventOptionsDropdown.selected = this.eventOptionsDropdown.rows.find( row => row.state?.key === this.bonusRule.event_value )?.state
  },
  methods: {
    updatedSelectedEvent(selectedEvent) {
      this.eventOptionsDropdown.selected = null
      this.bonusRule.event_value = null
      this.bonusRule.event_range_min = null
      this.bonusRule.event_range_max = null
      this.bonusRule.event_type = selectedEvent.key
    },
    updateSelectedEventOption(selectedEventOption) {
      this.bonusRule.event_value = selectedEventOption.key
    },
    computeEventOptionsDropdownRows() {
      if (!Array.isArray(this.eventValues)) {
        this.errorMessage = "error, please refresh or contact support"
        return []
      }
      this.eventOptionsDropdown.rows = this.eventValues.map( row => {
        return {
          slot: 'select-row',
          state: row
        }
      });
      this.errorMessage = null
    },
    preventNegativeNumber(event) {
      if (event.key === '-') {
        event.preventDefault()
      }
    },
    moveRuleUp() {
      this.$emit('increasePriority', this.bonusRule);
    },
    moveRuleDown() {
      this.$emit('decreasePriority', this.bonusRule);
    }
  },
  watch: {
    isRuleValid: function (val) {
      this.bonusRule.is_valid = val
    },
    eventValues: function (val) {
      if(this.inputType==='select') this.computeEventOptionsDropdownRows();
    },
  }
}
</script>

<style lang="scss" scoped>
.dropdown-component::v-deep {
  min-width: 278px;
  &.focused main.label-container {
    border-bottom: 0;
  }
  main.label-container {
    border: 1px solid #128ff9;
    color: #128ff9;
    background-color: #fff;
    padding: 0.5rem 1rem;
    font-family: AileronLight, sans-serif;
    align-items: normal;
    height:45px;
    >div {
      justify-content: space-between;
    }
  }
  .sb-content {
    border-top: 0;
  }
}

.sb-date::v-deep .label-container {
  white-space: nowrap;
  height:45px;
  padding-top:7px;
}

.dropdown-component.hasError::v-deep,
.sb-date.hasError::v-deep {
  .label-container {
    border-color: red;
    color: red;
  }
  .sb-content {
    border-color: red;
  }
}

.bonus-rule input{
  height: 45px;
  width: 140px;
  &.hasError {
    border-color: red;
    color: red;
  }
}

.priority {
  >.chevron {
    color: #128ff9;
    cursor: pointer;
    &.disabled {
      color: #aaa;
      cursor: default;
    }
  }
}
</style>
